import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { Loader2, Copy, Trash2, Upload, ExternalLink, LogOut, Film, Eye, Image as ImageIcon, X, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CHUNK_SIZE = 4 * 1024 * 1024;

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("fp_token") || ""}` });

const fieldClass =
  "w-full bg-transparent border-b border-foreground/20 py-3 text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:border-foreground transition-colors duration-300";

const btnDark =
  "inline-flex items-center gap-2 bg-[#1A1A1A] text-[#F7F5F0] px-8 py-3 text-xs tracking-[0.18em] uppercase hover:bg-accent transition-colors duration-300 disabled:opacity-50";

const StatusBadge = ({ status }) => {
  const map = {
    awaiting_video: ["No video yet", "text-muted-foreground border-foreground/20"],
    processing: ["Processing…", "text-accent border-accent/40"],
    ready: ["Live", "text-foreground border-foreground/40"],
    error: ["Upload failed", "text-red-600 border-red-300"],
  };
  const [label, cls] = map[status] || map.awaiting_video;
  return (
    <span className={`text-[10px] tracking-[0.15em] uppercase border px-2.5 py-1 ${cls}`}>
      {label}
    </span>
  );
};

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem("fp_token", data.access_token);
      onLogin();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <form onSubmit={submit} data-testid="admin-login-form" className="w-full max-w-sm">
        <p className="overline mb-6">Form Pathway</p>
        <h1 className="font-display font-light text-4xl mb-10">The Studio.</h1>
        <div className="space-y-7">
          <input
            data-testid="admin-email"
            className={fieldClass}
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <input
            data-testid="admin-password"
            className={fieldClass}
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <button data-testid="admin-login-submit" className={btnDark} disabled={loading}>
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            Sign In
          </button>
        </div>
      </form>
    </div>
  );
};

const UploadButton = ({ demo, onStarted, onProgress }) => {
  const inputRef = useRef(null);
  const [progress, setProgress] = useState(null);

  const pick = () => inputRef.current?.click();

  const setP = (v) => {
    setProgress(v);
    onProgress?.(demo.id, v);
  };

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 500 * 1024 * 1024) {
      toast.error("File too large (max 500MB)");
      return;
    }
    try {
      setP(0);
      const { data: initData } = await axios.post(
        `${API}/admin/demos/${demo.id}/upload/init`, {}, { headers: authHeaders() }
      );
      const uploadId = initData.upload_id;
      const total = Math.ceil(file.size / CHUNK_SIZE);
      for (let i = 0; i < total; i++) {
        const blob = file.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
        const fd = new FormData();
        fd.append("upload_id", uploadId);
        fd.append("index", i);
        fd.append("chunk", blob, "chunk.bin");
        await axios.post(`${API}/admin/demos/${demo.id}/upload/chunk`, fd, { headers: authHeaders() });
        setP(Math.round(((i + 1) / total) * 100));
      }
      const fd = new FormData();
      fd.append("upload_id", uploadId);
      await axios.post(`${API}/admin/demos/${demo.id}/upload/complete`, fd, { headers: authHeaders() });
      toast.success("Upload complete - preparing your film…");
      onStarted();
    } catch (err) {
      toast.error("Upload failed. Please try again.");
    } finally {
      setP(null);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept="video/mp4,video/quicktime,video/*"
        className="hidden"
        data-testid={`demo-file-input-${demo.slug}`}
        onChange={(e) => upload(e.target.files?.[0])}
      />
      <button
        data-testid={`demo-upload-btn-${demo.slug}`}
        onClick={pick}
        disabled={progress !== null || demo.status === "processing"}
        className="inline-flex items-center gap-2 text-xs tracking-[0.15em] uppercase border border-foreground/30 px-4 py-2 hover:border-foreground transition-colors disabled:opacity-50"
      >
        {progress !== null ? (
          <>
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> {progress}%
          </>
        ) : (
          <>
            <Upload strokeWidth={1.5} className="w-3.5 h-3.5" />
            {demo.status === "ready" ? "Replace video" : "Upload video"}
          </>
        )}
      </button>
    </>
  );
};

const ThumbnailPicker = ({ demo, onClose, onSaved }) => {
  const [posters, setPosters] = useState(null);
  const [selected, setSelected] = useState(0);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/admin/demos/${demo.id}/posters`, { headers: authHeaders() })
      .then(({ data }) => {
        if (!alive) return;
        setPosters(data.posters);
        setSelected(data.selected ?? 0);
      })
      .catch(() => {
        toast.error("Could not load thumbnails");
        onClose();
      });
    return () => {
      alive = false;
    };
  }, [demo.id, onClose]);

  const choose = async (index) => {
    setSelected(index);
    setSaving(true);
    try {
      await axios.post(
        `${API}/admin/demos/${demo.id}/poster`,
        { index },
        { headers: authHeaders() }
      );
      toast.success("Thumbnail updated");
      onSaved();
    } catch {
      toast.error("Could not update thumbnail");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4 py-8"
      data-testid="thumbnail-picker"
      onClick={onClose}
    >
      <div
        className="bg-background border border-border w-full max-w-3xl max-h-[88vh] overflow-y-auto p-7 sm:p-9"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-2">
          <div>
            <p className="overline">Choose Thumbnail</p>
            <h2 className="font-display font-light text-3xl mt-2">{demo.title}</h2>
          </div>
          <button
            data-testid="thumbnail-picker-close"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors p-1"
          >
            <X strokeWidth={1.5} className="w-5 h-5" />
          </button>
        </div>
        <p className="text-sm text-muted-foreground mb-7">
          Pick the frame viewers see before pressing play.
        </p>
        {posters === null ? (
          <div className="py-16 flex justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : posters.length === 0 ? (
          <p className="text-sm text-muted-foreground py-10 text-center">
            No thumbnails available for this film yet.
          </p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {posters.map((p) => (
              <button
                key={p.index}
                data-testid={`thumbnail-option-${p.index}`}
                onClick={() => choose(p.index)}
                disabled={saving}
                className={`relative group overflow-hidden border-2 transition-colors ${
                  selected === p.index ? "border-accent" : "border-transparent hover:border-foreground/40"
                }`}
              >
                <img src={p.data_url} alt={`Frame ${p.index + 1}`} className="w-full aspect-video object-cover block" />
                {selected === p.index && (
                  <span className="absolute top-2 right-2 bg-accent text-white rounded-full p-1">
                    <Check strokeWidth={2.5} className="w-3.5 h-3.5" />
                  </span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default function StudioAdmin() {
  const [authed, setAuthed] = useState(null);
  const [demos, setDemos] = useState([]);
  const [slug, setSlug] = useState("");
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [creating, setCreating] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [pwCurrent, setPwCurrent] = useState("");
  const [pwNew, setPwNew] = useState("");
  const [uploadProgress, setUploadProgress] = useState({});
  const [posterDemo, setPosterDemo] = useState(null);

  const handleProgress = useCallback((id, v) => {
    setUploadProgress((p) => ({ ...p, [id]: v }));
  }, []);

  useEffect(() => {
    document.title = "Studio - Form Pathway";
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => meta.remove();
  }, []);

  const loadDemos = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/demos`, { headers: authHeaders() });
      setDemos(data);
    } catch {
      setAuthed(false);
      localStorage.removeItem("fp_token");
    }
  }, []);

  useEffect(() => {
    axios
      .get(`${API}/auth/me`, { headers: authHeaders() })
      .then(() => setAuthed(true))
      .catch(() => setAuthed(false));
  }, []);

  useEffect(() => {
    if (authed) loadDemos();
  }, [authed, loadDemos]);

  useEffect(() => {
    if (!authed || !demos.some((d) => d.status === "processing")) return;
    const t = setInterval(loadDemos, 4000);
    return () => clearInterval(t);
  }, [authed, demos, loadDemos]);

  const createDemo = async (e) => {
    e.preventDefault();
    if (!slug || !title) {
      toast.error("Link name and title are required");
      return;
    }
    setCreating(true);
    try {
      await axios.post(
        `${API}/admin/demos`,
        { slug, title, message },
        { headers: authHeaders() }
      );
      setSlug("");
      setTitle("");
      setMessage("");
      toast.success("Demo link created - now upload its video");
      loadDemos();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not create demo");
    } finally {
      setCreating(false);
    }
  };

  const deleteDemo = async (demo) => {
    if (!window.confirm(`Delete the demo "${demo.title}"? The link will stop working.`)) return;
    await axios.delete(`${API}/admin/demos/${demo.id}`, { headers: authHeaders() });
    toast.success("Demo deleted");
    loadDemos();
  };

  const copyLink = (demo) => {
    navigator.clipboard.writeText(`${window.location.origin}/d/${demo.slug}`);
    toast.success("Link copied");
  };

  const resetViews = async (demo) => {
    if (!window.confirm(`Reset the view count for "${demo.title}" to zero?`)) return;
    try {
      await axios.post(`${API}/admin/demos/${demo.id}/views/reset`, {}, { headers: authHeaders() });
      toast.success("View count reset");
      loadDemos();
    } catch {
      toast.error("Could not reset views");
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `${API}/auth/change-password`,
        { current_password: pwCurrent, new_password: pwNew },
        { headers: authHeaders() }
      );
      toast.success("Password updated");
      setPwCurrent("");
      setPwNew("");
      setShowPw(false);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not update password");
    }
  };

  if (authed === null)
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );

  if (!authed)
    return (
      <div className="bg-background text-foreground min-h-screen">
        <Toaster position="top-center" />
        <Login onLogin={() => setAuthed(true)} />
      </div>
    );

  return (
    <div className="bg-background text-foreground min-h-screen" data-testid="admin-dashboard">
      <Toaster position="top-center" />
      <header className="border-b border-border">
        <div className="max-w-5xl mx-auto px-6 py-5 flex items-center justify-between">
          <a href="/" className="font-display text-xl tracking-tight">
            Form Pathway<span className="text-accent">.</span>
          </a>
          <div className="flex items-center gap-6">
            <button
              data-testid="admin-change-password-toggle"
              onClick={() => setShowPw((s) => !s)}
              className="text-xs tracking-[0.15em] uppercase text-muted-foreground hover:text-foreground transition-colors"
            >
              Password
            </button>
            <button
              data-testid="admin-logout"
              onClick={() => {
                localStorage.removeItem("fp_token");
                setAuthed(false);
              }}
              className="inline-flex items-center gap-2 text-xs tracking-[0.15em] uppercase text-muted-foreground hover:text-foreground transition-colors"
            >
              <LogOut strokeWidth={1.5} className="w-3.5 h-3.5" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-14">
        {showPw && (
          <form onSubmit={changePassword} className="mb-14 max-w-sm space-y-5" data-testid="change-password-form">
            <p className="overline">Change Password</p>
            <input
              data-testid="pw-current"
              className={fieldClass}
              type="password"
              placeholder="Current password"
              value={pwCurrent}
              onChange={(e) => setPwCurrent(e.target.value)}
            />
            <input
              data-testid="pw-new"
              className={fieldClass}
              type="password"
              placeholder="New password (min 8 characters)"
              value={pwNew}
              onChange={(e) => setPwNew(e.target.value)}
            />
            <button data-testid="pw-submit" className={btnDark}>Update</button>
          </form>
        )}

        <p className="overline mb-4">Private Screenings</p>
        <h1 className="font-display font-light text-4xl sm:text-5xl mb-12">Demo Links.</h1>

        <form onSubmit={createDemo} data-testid="create-demo-form" className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-7 mb-16 border border-border p-7 sm:p-9">
          <div>
            <input
              data-testid="demo-slug-input"
              className={fieldClass}
              placeholder="Link name, e.g. savills-demo *"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
            />
            <p className="mt-2 text-xs text-muted-foreground">
              {window.location.origin}/d/{slug ? slug.toLowerCase().replace(/[^a-z0-9-]+/g, "-") : "your-link-name"}
            </p>
          </div>
          <input
            data-testid="demo-title-input"
            className={fieldClass}
            placeholder="Title, e.g. Prepared for Savills *"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
          <textarea
            data-testid="demo-message-input"
            rows={2}
            className={`${fieldClass} sm:col-span-2 resize-none`}
            placeholder="Message shown above the film (optional)"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />
          <div className="sm:col-span-2">
            <button data-testid="create-demo-submit" className={btnDark} disabled={creating}>
              {creating && <Loader2 className="w-4 h-4 animate-spin" />}
              Create Demo Link
            </button>
          </div>
        </form>

        <div className="space-y-5" data-testid="demo-list">
          {demos.length === 0 && (
            <p className="text-muted-foreground text-sm">No demo links yet - create your first one above.</p>
          )}
          {demos.map((demo) => (
            <div
              key={demo.id}
              data-testid={`demo-row-${demo.slug}`}
              className="border border-border p-6"
            >
              <div className="flex flex-col sm:flex-row sm:items-center gap-5 sm:justify-between">
                <div className="min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <Film strokeWidth={1.25} className="w-4 h-4 text-muted-foreground shrink-0" />
                    <h3 className="font-display text-2xl leading-none truncate">{demo.title}</h3>
                    <StatusBadge status={demo.status} />
                  </div>
                  <div className="mt-2 flex items-center gap-4 flex-wrap">
                    <p className="text-sm text-muted-foreground truncate">/d/{demo.slug}</p>
                    {demo.status === "ready" && (
                      <span
                        data-testid={`demo-views-${demo.slug}`}
                        className="inline-flex items-center gap-1.5 text-xs text-muted-foreground"
                      >
                        <Eye strokeWidth={1.5} className="w-3.5 h-3.5" />
                        {demo.views} {demo.views === 1 ? "view" : "views"}
                        {demo.views > 0 && (
                          <button
                            data-testid={`demo-views-reset-${demo.slug}`}
                            onClick={() => resetViews(demo)}
                            title="Reset view count to zero"
                            className="ml-1 text-muted-foreground/60 hover:text-foreground transition-colors underline underline-offset-2"
                          >
                            reset
                          </button>
                        )}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2.5 flex-wrap shrink-0">
                  <UploadButton demo={demo} onStarted={loadDemos} onProgress={handleProgress} />
                  {demo.status === "ready" && demo.poster_count > 1 && (
                    <button
                      data-testid={`demo-thumbnail-${demo.slug}`}
                      onClick={() => setPosterDemo(demo)}
                      className="inline-flex items-center gap-2 text-xs tracking-[0.15em] uppercase border border-foreground/30 px-4 py-2 hover:border-foreground transition-colors"
                    >
                      <ImageIcon strokeWidth={1.5} className="w-3.5 h-3.5" /> Thumbnail
                    </button>
                  )}
                  <button
                    data-testid={`demo-copy-${demo.slug}`}
                    onClick={() => copyLink(demo)}
                    className="inline-flex items-center gap-2 text-xs tracking-[0.15em] uppercase border border-foreground/30 px-4 py-2 hover:border-foreground transition-colors"
                  >
                    <Copy strokeWidth={1.5} className="w-3.5 h-3.5" /> Copy link
                  </button>
                  <a
                    data-testid={`demo-open-${demo.slug}`}
                    href={`/d/${demo.slug}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 text-xs tracking-[0.15em] uppercase border border-foreground/30 px-4 py-2 hover:border-foreground transition-colors"
                  >
                    <ExternalLink strokeWidth={1.5} className="w-3.5 h-3.5" /> Open
                  </a>
                  <button
                    data-testid={`demo-delete-${demo.slug}`}
                    onClick={() => deleteDemo(demo)}
                    className="inline-flex items-center gap-2 text-xs tracking-[0.15em] uppercase border border-red-300 text-red-600 px-4 py-2 hover:border-red-500 transition-colors"
                  >
                    <Trash2 strokeWidth={1.5} className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>

              {uploadProgress[demo.id] != null && (
                <div className="mt-5" data-testid={`demo-upload-progress-${demo.slug}`}>
                  <div className="flex justify-between text-xs text-muted-foreground mb-1.5">
                    <span>Uploading your film…</span>
                    <span>{uploadProgress[demo.id]}%</span>
                  </div>
                  <div className="h-1 bg-foreground/10 overflow-hidden">
                    <div
                      className="h-full bg-accent transition-all duration-300"
                      style={{ width: `${uploadProgress[demo.id]}%` }}
                    />
                  </div>
                </div>
              )}

              {uploadProgress[demo.id] == null && demo.status === "processing" && (
                <div
                  className="mt-5 flex items-center gap-2.5 text-xs text-muted-foreground"
                  data-testid={`demo-processing-${demo.slug}`}
                >
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Preparing your film — this usually takes under a minute.
                </div>
              )}
            </div>
          ))}
        </div>
      </main>

      {posterDemo && (
        <ThumbnailPicker
          demo={posterDemo}
          onClose={() => setPosterDemo(null)}
          onSaved={loadDemos}
        />
      )}
    </div>
  );
}
